import {
  EnvironmentProviders,
  InjectionToken,
  Optional,
  makeEnvironmentProviders,
} from '@angular/core';
import type { Auth as FbAuth } from 'firebase/auth';
import {
  applyActionCode as rawApplyActionCode,
  beforeAuthStateChanged as rawBeforeAuthStateChanged,
  checkActionCode as rawCheckActionCode,
  confirmPasswordReset as rawConfirmPasswordReset,
  connectAuthEmulator as rawConnectAuthEmulator,
  createUserWithEmailAndPassword as rawCreateUserWithEmailAndPassword,
  deleteUser as rawDeleteUser,
  fetchSignInMethodsForEmail as rawFetchSignInMethodsForEmail,
  getAdditionalUserInfo as rawGetAdditionalUserInfo,
  getAuth as rawGetAuth,
  getIdToken as rawGetIdToken,
  getIdTokenResult as rawGetIdTokenResult,
  getMultiFactorResolver as rawGetMultiFactorResolver,
  getRedirectResult as rawGetRedirectResult,
  initializeAuth as rawInitializeAuth,
  initializeRecaptchaConfig as rawInitializeRecaptchaConfig,
  isSignInWithEmailLink as rawIsSignInWithEmailLink,
  linkWithCredential as rawLinkWithCredential,
  linkWithPhoneNumber as rawLinkWithPhoneNumber,
  linkWithPopup as rawLinkWithPopup,
  linkWithRedirect as rawLinkWithRedirect,
  onAuthStateChanged as rawOnAuthStateChanged,
  onIdTokenChanged as rawOnIdTokenChanged,
  parseActionCodeURL as rawParseActionCodeURL,
  reauthenticateWithCredential as rawReauthenticateWithCredential,
  reauthenticateWithPhoneNumber as rawReauthenticateWithPhoneNumber,
  reauthenticateWithPopup as rawReauthenticateWithPopup,
  reauthenticateWithRedirect as rawReauthenticateWithRedirect,
  reload as rawReload,
  revokeAccessToken as rawRevokeAccessToken,
  sendEmailVerification as rawSendEmailVerification,
  sendPasswordResetEmail as rawSendPasswordResetEmail,
  sendSignInLinkToEmail as rawSendSignInLinkToEmail,
  setPersistence as rawSetPersistence,
  signInAnonymously as rawSignInAnonymously,
  signInWithCredential as rawSignInWithCredential,
  signInWithCustomToken as rawSignInWithCustomToken,
  signInWithEmailAndPassword as rawSignInWithEmailAndPassword,
  signInWithEmailLink as rawSignInWithEmailLink,
  signInWithPhoneNumber as rawSignInWithPhoneNumber,
  signInWithPopup as rawSignInWithPopup,
  signInWithRedirect as rawSignInWithRedirect,
  signOut as rawSignOut,
  unlink as rawUnlink,
  updateCurrentUser as rawUpdateCurrentUser,
  updateEmail as rawUpdateEmail,
  updatePassword as rawUpdatePassword,
  updatePhoneNumber as rawUpdatePhoneNumber,
  updateProfile as rawUpdateProfile,
  useDeviceLanguage as rawUseDeviceLanguage,
  validatePassword as rawValidatePassword,
  verifyBeforeUpdateEmail as rawVerifyBeforeUpdateEmail,
  verifyPasswordResetCode as rawVerifyPasswordResetCode,
} from 'firebase/auth';
import { kdvWrap } from '@fido/firebase';
import { FirebaseApp } from '@fido/firebase/app';
import { AppCheck } from '@fido/firebase/app-check';
import {
  authState as vendorAuthState,
  user as vendorUser,
  idToken as vendorIdToken,
} from './lib/vendor';

// 1) Wrapped firebase/auth functions. All use blockUntilFirst=true, matching
//    @angular/fire's _zoneWrap declarations from angular-fire-auth.mjs.
export const applyActionCode = kdvWrap(rawApplyActionCode, true);
export const beforeAuthStateChanged = kdvWrap(rawBeforeAuthStateChanged, true);
export const checkActionCode = kdvWrap(rawCheckActionCode, true);
export const confirmPasswordReset = kdvWrap(rawConfirmPasswordReset, true);
export const connectAuthEmulator = kdvWrap(rawConnectAuthEmulator, true);
export const createUserWithEmailAndPassword = kdvWrap(rawCreateUserWithEmailAndPassword, true);
export const deleteUser = kdvWrap(rawDeleteUser, true);
export const fetchSignInMethodsForEmail = kdvWrap(rawFetchSignInMethodsForEmail, true);
export const getAdditionalUserInfo = kdvWrap(rawGetAdditionalUserInfo, true);
export const getAuth = kdvWrap(rawGetAuth, true);
export const getIdToken = kdvWrap(rawGetIdToken, true);
export const getIdTokenResult = kdvWrap(rawGetIdTokenResult, true);
export const getMultiFactorResolver = kdvWrap(rawGetMultiFactorResolver, true);
export const getRedirectResult = kdvWrap(rawGetRedirectResult, true);
export const initializeAuth = kdvWrap(rawInitializeAuth, true);
export const initializeRecaptchaConfig = kdvWrap(rawInitializeRecaptchaConfig, true);
export const isSignInWithEmailLink = kdvWrap(rawIsSignInWithEmailLink, true);
export const linkWithCredential = kdvWrap(rawLinkWithCredential, true);
export const linkWithPhoneNumber = kdvWrap(rawLinkWithPhoneNumber, true);
export const linkWithPopup = kdvWrap(rawLinkWithPopup, true);
export const linkWithRedirect = kdvWrap(rawLinkWithRedirect, true);
export const onAuthStateChanged = kdvWrap(rawOnAuthStateChanged, true);
export const onIdTokenChanged = kdvWrap(rawOnIdTokenChanged, true);
export const parseActionCodeURL = kdvWrap(rawParseActionCodeURL, true);
export const reauthenticateWithCredential = kdvWrap(rawReauthenticateWithCredential, true);
export const reauthenticateWithPhoneNumber = kdvWrap(rawReauthenticateWithPhoneNumber, true);
export const reauthenticateWithPopup = kdvWrap(rawReauthenticateWithPopup, true);
export const reauthenticateWithRedirect = kdvWrap(rawReauthenticateWithRedirect, true);
export const reload = kdvWrap(rawReload, true);
export const revokeAccessToken = kdvWrap(rawRevokeAccessToken, true);
export const sendEmailVerification = kdvWrap(rawSendEmailVerification, true);
export const sendPasswordResetEmail = kdvWrap(rawSendPasswordResetEmail, true);
export const sendSignInLinkToEmail = kdvWrap(rawSendSignInLinkToEmail, true);
export const setPersistence = kdvWrap(rawSetPersistence, true);
export const signInAnonymously = kdvWrap(rawSignInAnonymously, true);
export const signInWithCredential = kdvWrap(rawSignInWithCredential, true);
export const signInWithCustomToken = kdvWrap(rawSignInWithCustomToken, true);
export const signInWithEmailAndPassword = kdvWrap(rawSignInWithEmailAndPassword, true);
export const signInWithEmailLink = kdvWrap(rawSignInWithEmailLink, true);
export const signInWithPhoneNumber = kdvWrap(rawSignInWithPhoneNumber, true);
export const signInWithPopup = kdvWrap(rawSignInWithPopup, true);
export const signInWithRedirect = kdvWrap(rawSignInWithRedirect, true);
export const signOut = kdvWrap(rawSignOut, true);
export const unlink = kdvWrap(rawUnlink, true);
export const updateCurrentUser = kdvWrap(rawUpdateCurrentUser, true);
export const updateEmail = kdvWrap(rawUpdateEmail, true);
export const updatePassword = kdvWrap(rawUpdatePassword, true);
export const updatePhoneNumber = kdvWrap(rawUpdatePhoneNumber, true);
export const updateProfile = kdvWrap(rawUpdateProfile, true);
export const useDeviceLanguage = kdvWrap(rawUseDeviceLanguage, true);
export const validatePassword = kdvWrap(rawValidatePassword, true);
export const verifyBeforeUpdateEmail = kdvWrap(rawVerifyBeforeUpdateEmail, true);
export const verifyPasswordResetCode = kdvWrap(rawVerifyPasswordResetCode, true);

// 2) Re-exports — provider classes (constructors, not wrappable) and
//    persistence constants (frozen objects).
export {
  GoogleAuthProvider,
  FacebookAuthProvider,
  GithubAuthProvider,
  TwitterAuthProvider,
  OAuthProvider,
  EmailAuthProvider,
  PhoneAuthProvider,
  browserLocalPersistence,
  browserSessionPersistence,
  indexedDBLocalPersistence,
  inMemoryPersistence,
} from 'firebase/auth';
export type {
  User,
  UserCredential,
  UserInfo,
  UserMetadata,
  AuthCredential,
  IdTokenResult,
  AuthProvider,
  Persistence,
  AuthError,
  ParsedToken,
} from 'firebase/auth';

// 3) DI token (declaration-merged with type alias). Same name as
//    @angular/fire so consumer call sites like `inject(Auth)` keep
//    compiling unchanged.
export type Auth = FbAuth;
export const Auth = new InjectionToken<FbAuth>('@fido/firebase.auth');

export function provideAuth(factory: () => FbAuth): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: Auth,
      // FirebaseApp + (Optional) AppCheck deps — mirrors @angular/fire's
      // provideAuth multi-instance deps which include
      // `[Optional(), AppCheckInstances]`. Defensively hydrating AppCheck
      // before Auth gives the App Check token mint a head start so that
      // signed-in callables don't race against AppCheck warmup.
      useFactory: (_app: unknown, _appCheck: unknown) => factory(),
      deps: [FirebaseApp, [new Optional(), AppCheck]],
    },
  ]);
}

// 4) Vendored rxfire helpers, wrapped via kdvWrap. Identical surface to
//    @angular/fire/auth's authState / user / idToken.
export const authState = kdvWrap(vendorAuthState, true);
export const user = kdvWrap(vendorUser, true);
export const idToken = kdvWrap(vendorIdToken, true);
